import { call, put, select, takeLatest } from 'redux-saga/effects'
import {
  LOAD_COMPARABLE_PROPERTIES,
  LOAD_COPYRIGHT,
  LOAD_DISCLAIMER,
  LOAD_LOCAL_SCHOOLS,
  LOAD_MAP,
  LOAD_MARKET_INSIGHTS,
  LOAD_PROPERTY_PHOTO,
  LOAD_PROPERTY_SUMMARY,
  LOAD_PROPERTY_TIMELINE,
  LOAD_RATING_VALUATION,
  LOAD_RECORD_OF_TITLE_DETAILS,
  LOAD_VALUATION_ESTIMATES,
  loadComparablePropertiesFailed,
  loadComparablePropertiesFinished,
  loadCopyrightFailed,
  loadCopyrightFinished,
  loadDisclaimerFailed,
  loadDisclaimerFinished,
  loadLocalSchoolsFailed,
  loadLocalSchoolsFinished,
  loadMapFailed,
  loadMapFinished,
  loadMarketInsightsFailed,
  loadMarketInsightsFinished,
  loadPropertyPhoto,
  loadPropertyPhotoFailed,
  loadPropertyPhotoFinished,
  loadPropertySummary,
  loadPropertySummaryFailed,
  loadPropertySummaryFinished,
  loadPropertyTimelineFailed,
  loadPropertyTimelineFinished,
  loadRatingValuationFailed,
  loadRatingValuationFinished,
  loadRecordOfTitleDetailsFailed,
  loadRecordOfTitleDetailsFinished,
  loadValuationEstimatesFailed,
  loadValuationEstimatesFinished,
  resetComparableProperties,
  resetLocalSchools,
  resetMap,
  resetMarketInsights,
  resetPropertyPhoto,
  resetPropertySummary,
  resetPropertyTimeline,
  resetRatingValuation,
  resetRecordOfTitleDetails,
  resetValuationEstimates
} from 'store/actions/clapiAction'
import {
  fetchComparableProperties,
  fetchCopyright,
  fetchDisclaimer,
  fetchLocalSchools,
  fetchMapCoordinates,
  fetchMarketInsights,
  fetchPropertyPhoto,
  fetchPropertySummary,
  fetchPropertyTimeline,
  fetchRatingValuation,
  fetchRecordOfTitleDetails,
  fetchValuationEstimates
} from 'api/reportApi'
import { LOAD_REPORT_PREVIEW } from 'store/actions/reportAction'
import { getConfigurationSelector } from 'store/selectors/configurationSelector'
import {
  PREVIEW_DATA_COMPARABLES_AU,
  PREVIEW_DATA_COMPARABLES_NZ,
  PREVIEW_DATA_COPYRIGHT,
  PREVIEW_DATA_DISCLAIMER_AU,
  PREVIEW_DATA_DISCLAIMER_NZ,
  PREVIEW_DATA_LOCAL_SCHOOL,
  PREVIEW_DATA_MAP_PANEL,
  PREVIEW_DATA_MARKET_INSIGHT_1YR_AU,
  PREVIEW_DATA_MARKET_INSIGHT_1YR_NZ,
  PREVIEW_DATA_PHOTO_LIST,
  PREVIEW_DATA_PROPERTY_SUMMARY_AU,
  PREVIEW_DATA_PROPERTY_SUMMARY_NZ,
  PREVIEW_DATA_PROPERTY_TIMELINE_AU,
  PREVIEW_DATA_PROPERTY_TIMELINE_NZ,
  PREVIEW_DATA_RATING_VALUATION,
  PREVIEW_DATA_TITLE_DETAIL,
  PREVIEW_DATA_VALUATION_ESTIMATE_AU,
  PREVIEW_DATA_VALUATION_ESTIMATE_NZ
} from 'constants/previewReportData'
import { Country } from 'utils/constants'

// UTILS //
const PROPERTY_PHOTO_PANEL_ID = 1
const PROPERTY_SUMMARY_PANEL_ID = 3

export const getIsPanelEnabledById = (state, id) => {
  const reportSections = state.preference.preferenceReportSections
  const panel = reportSections.find(({ reportSectionId }) => reportSectionId === id)

  return panel && panel.enabled
}

// WORKERS //
export function * loadPropertyPhotoHandler(action) {
  const code = action.payload

  try {
    yield put(resetPropertyPhoto())
    const response = yield call(fetchPropertyPhoto, code)

    yield put(loadPropertyPhotoFinished(response.data))
  } catch (e) {
    yield put(loadPropertyPhotoFailed(e.code))
  }
}

export function * loadPropertySummaryHandler(action) {
  const code = action.payload

  try {
    yield put(resetPropertySummary())
    const response = yield call(fetchPropertySummary, code)

    yield put(loadPropertySummaryFinished(response.data))
  } catch (e) {
    yield put(loadPropertySummaryFailed(e.code))
  }
}

export function * loadValuationEstimatesHandler(action) {
  const code = action.payload

  try {
    yield put(resetValuationEstimates())
    const response = yield call(fetchValuationEstimates, code)

    yield put(loadValuationEstimatesFinished(response.data))
  } catch (e) {
    yield put(loadValuationEstimatesFailed(e.code))
  }
}

export function * loadRatingValuationHandler(action) {
  const code = action.payload

  try {
    yield put(resetRatingValuation())
    const response = yield call(fetchRatingValuation, code)

    yield put(loadRatingValuationFinished(response.data))
  } catch (e) {
    yield put(loadRatingValuationFailed(e.code))
  }
}

export function * loadRecordOfTitleDetailsHandler(action) {
  const code = action.payload

  try {
    yield put(resetRecordOfTitleDetails())
    const response = yield call(fetchRecordOfTitleDetails, code)

    yield put(loadRecordOfTitleDetailsFinished(response.data))
  } catch (e) {
    yield put(loadRecordOfTitleDetailsFailed(e.code))
  }
}

export function * loadDisclaimerHandler(action) {
  const code = action.payload

  try {
    const response = yield call(fetchDisclaimer, code)

    yield put(loadDisclaimerFinished(response.data))
  } catch (e) {
    yield put(loadDisclaimerFailed(e.code))
  }
}

export function * loadCopyrightHandler(action) {
  const code = action.payload

  try {
    const response = yield call(fetchCopyright, code)

    yield put(loadCopyrightFinished(response.data))
  } catch (e) {
    yield put(loadCopyrightFailed(e.code))
  }
}

export function * loadPropertyTimelineHandler(action) {
  const code = action.payload

  try {
    yield put(resetPropertyTimeline())
    const response = yield call(fetchPropertyTimeline, code)

    yield put(loadPropertyTimelineFinished(response.data))
  } catch (e) {
    yield put(loadPropertyTimelineFailed(e.code))
  }
}

export function * loadComparablePropertiesHandler(action) {
  const code = action.payload

  try {
    yield put(resetComparableProperties())

    const isPropertyPanelEnabled = yield select(getIsPanelEnabledById, PROPERTY_SUMMARY_PANEL_ID)
    if (!isPropertyPanelEnabled) {
      yield put(loadPropertySummary(code))
    }

    const isPhotoPanelEnabled = yield select(getIsPanelEnabledById, PROPERTY_PHOTO_PANEL_ID)
    if (!isPhotoPanelEnabled) {
      yield put(loadPropertyPhoto(code))
    }

    const response = yield call(fetchComparableProperties, code)

    yield put(loadComparablePropertiesFinished(response.data))
  } catch (e) {
    yield put(loadComparablePropertiesFailed(e.code))
  }
}

export function * loadLocalSchoolsHandler(action) {
  const code = action.payload

  try {
    yield put(resetLocalSchools())

    const isPropertyPanelEnabled = yield select(getIsPanelEnabledById, PROPERTY_SUMMARY_PANEL_ID)
    if (!isPropertyPanelEnabled) {
      yield put(loadPropertySummary(code))
    }

    const response = yield call(fetchLocalSchools, code)

    yield put(loadLocalSchoolsFinished(response.data))
  } catch (e) {
    yield put(loadLocalSchoolsFailed(e.code))
  }
}

export function * loadMarketInsightsHandler(action) {
  const { code, yearCount } = action.payload
  try {
    yield put(resetMarketInsights(yearCount))
    const response = yield call(fetchMarketInsights, code, yearCount)
    yield put(loadMarketInsightsFinished({ data: response.data, yearCount }))
  } catch (e) {
    yield put(loadMarketInsightsFailed({ code: e.code, yearCount }))
  }
}

export function * loadMapHandler(action) {
  const code = action.payload

  try {
    yield put(resetMap())
    const response = yield call(fetchMapCoordinates, code)

    yield put(loadMapFinished(response.data))
  } catch (e) {
    yield put(loadMapFailed(e.code))
  }
}

export function * loadReportPreviewHandler(action) {
  const { countryCode } = action.payload

  try {
    const { publicUrl } = yield select(getConfigurationSelector)
    const propertyPhotos = PREVIEW_DATA_PHOTO_LIST(publicUrl)

    if (countryCode === Country.AU) {
      yield put(loadPropertyTimelineFinished(PREVIEW_DATA_PROPERTY_TIMELINE_AU))
      yield put(loadPropertyPhotoFinished(propertyPhotos))
      yield put(loadMarketInsightsFinished(PREVIEW_DATA_MARKET_INSIGHT_1YR_AU))
      yield put(loadDisclaimerFinished(PREVIEW_DATA_DISCLAIMER_AU))
      yield put(loadValuationEstimatesFinished(PREVIEW_DATA_VALUATION_ESTIMATE_AU))
      yield put(loadPropertySummaryFinished(PREVIEW_DATA_PROPERTY_SUMMARY_AU))
      yield put(loadComparablePropertiesFinished(PREVIEW_DATA_COMPARABLES_AU(publicUrl)))
    } else if (countryCode === Country.NZ) {
      propertyPhotos.pop()
      yield put(loadPropertyPhotoFinished(propertyPhotos))
      yield put(loadPropertyTimelineFinished(PREVIEW_DATA_PROPERTY_TIMELINE_NZ))
      yield put(loadRatingValuationFinished(PREVIEW_DATA_RATING_VALUATION))
      yield put(loadRecordOfTitleDetailsFinished(PREVIEW_DATA_TITLE_DETAIL))
      yield put(loadMarketInsightsFinished(PREVIEW_DATA_MARKET_INSIGHT_1YR_NZ))
      yield put(loadDisclaimerFinished(PREVIEW_DATA_DISCLAIMER_NZ))
      yield put(loadValuationEstimatesFinished(PREVIEW_DATA_VALUATION_ESTIMATE_NZ))
      yield put(loadPropertySummaryFinished(PREVIEW_DATA_PROPERTY_SUMMARY_NZ))
      yield put(loadComparablePropertiesFinished(PREVIEW_DATA_COMPARABLES_NZ(publicUrl)))
    }

    yield put(loadMapFinished(PREVIEW_DATA_MAP_PANEL))
    yield put(loadLocalSchoolsFinished(PREVIEW_DATA_LOCAL_SCHOOL))
    yield put(loadCopyrightFinished(PREVIEW_DATA_COPYRIGHT))
  } catch (e) {
    // nothing to do
  }
}

// WATCHER //
export default function * watchClapiSaga() {
  yield takeLatest(LOAD_PROPERTY_PHOTO, loadPropertyPhotoHandler)
  yield takeLatest(LOAD_PROPERTY_SUMMARY, loadPropertySummaryHandler)
  yield takeLatest(LOAD_VALUATION_ESTIMATES, loadValuationEstimatesHandler)
  yield takeLatest(LOAD_RECORD_OF_TITLE_DETAILS, loadRecordOfTitleDetailsHandler)
  yield takeLatest(LOAD_RATING_VALUATION, loadRatingValuationHandler)
  yield takeLatest(LOAD_DISCLAIMER, loadDisclaimerHandler)
  yield takeLatest(LOAD_COPYRIGHT, loadCopyrightHandler)
  yield takeLatest(LOAD_PROPERTY_TIMELINE, loadPropertyTimelineHandler)
  yield takeLatest(LOAD_COMPARABLE_PROPERTIES, loadComparablePropertiesHandler)
  yield takeLatest(LOAD_LOCAL_SCHOOLS, loadLocalSchoolsHandler)
  yield takeLatest(LOAD_MARKET_INSIGHTS, loadMarketInsightsHandler)
  yield takeLatest(LOAD_MAP, loadMapHandler)
  yield takeLatest(LOAD_REPORT_PREVIEW, loadReportPreviewHandler)
}
